import { useMemo } from 'react'
import { useMasters } from '../../hooks/useMasters'
import type { MasterKey, MasterOption } from '../../services/mastersService'

export type PayorSelectOption = { value: string; label: string }

/** Payor configuration payloads use display labels (e.g. "In-network PPO", "365 days"). */
export function mastersToSelectOptions(
  masters: MasterOption[],
  fallback: readonly PayorSelectOption[]
): PayorSelectOption[] {
  if (masters.length === 0) return [...fallback]
  return masters.map((item) => ({
    value: item.label,
    label: item.label,
  }))
}

export function mastersToLabelList(
  masters: MasterOption[],
  fallback: readonly string[]
): string[] {
  if (masters.length === 0) return [...fallback]
  return masters.map((item) => item.label)
}

export function mastersToSelectOptionsWithEmpty(
  masters: MasterOption[],
  fallback: readonly PayorSelectOption[],
  emptyOption: PayorSelectOption = { value: '', label: '—' }
): PayorSelectOption[] {
  const options = mastersToSelectOptions(masters, fallback)
  if (options.some((option) => option.value === '')) return options
  return [emptyOption, ...options.filter((option) => option.value !== '')]
}

export function usePayorMasterSelect(
  key: MasterKey,
  fallback: readonly PayorSelectOption[]
) {
  const { data, loading, error, reload } = useMasters(key)
  const options = useMemo(() => mastersToSelectOptions(data, fallback), [data, fallback])
  return { options, loading, error, reload, data }
}

/** Master keys used across the Add / Edit Payor wizard. */
export const PAYOR_WIZARD_MASTER_KEYS = {
  overview: ['contract-types', 'billing-states', 'submission-methods'] as const,
  plans: [
    'plan-types',
    'networks',
    'timely-filing-limits',
    'corrected-claim-deadlines',
    'coordination-of-benefits',
    'assignment-of-benefits',
    'claim-frequency-types',
    'rendering-provider-required',
  ] as const,
  feeSchedules: ['modifiers', 'fee-basis'] as const,
  cappedRentals: ['rental-cap-periods', 'cap-resets-on', 'equipment-categories'] as const,
  ownershipTransfer: [
    'transfer-triggers',
    'ownership-transfer-modifiers',
    'documentation-required',
  ] as const,
  priorAuth: ['pa-submission-methods', 'standard-turnaround-times'] as const,
}
